import { useState, useCallback, useMemo } from 'react';
import { findCategoryPath } from './useNodePath';
import Fuse from 'fuse.js';
import { SEGMENT_TYPE } from '../../../constants/radar';

const useSearch = (data, selectedSubcategory) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isGlobalSearch, setIsGlobalSearch] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Build a path map for each node to store its full path
  const nodePathMap = useMemo(() => {
    const pathMap = new Map();
    
    const buildPathMap = (nodes, parentPath = []) => {
      if (!nodes || !Array.isArray(nodes)) return;
      
      nodes.forEach(node => {
        if (!node) return;
        
        const currentPath = [...parentPath, node];
        pathMap.set(node.id, currentPath);
        
        if (node.subcategories) {
          buildPathMap(node.subcategories, currentPath);
        }
      });
    };
    
    buildPathMap(data.subcategories || []);
    return pathMap;
  }, [data]);

  const { searchResults, showNoResultsMessage } = useMemo(() => {
    // Ensure searchTerm is a string and not null/undefined
    if (searchTerm === null || searchTerm === undefined) {
      return { 
        searchResults: [], 
        showNoResultsMessage: false 
      };
    }
    
    // Return empty array if search term is empty or just whitespace
    const trimmedSearchTerm = (searchTerm || '').trim();
    if (!trimmedSearchTerm) {
      return { 
        searchResults: [], 
        showNoResultsMessage: false
      };
    }

    const flattenTree = (nodes, parentPath = [], results = []) => {
      if (!nodes || !Array.isArray(nodes) || nodes.length === 0) return results;
      
      nodes.forEach(node => {
        if (!node) return;
        
        // Build the current path for this node
        const currentPath = [...parentPath, node];
        const pathString = currentPath.length > 1 
          ? currentPath.slice(0, -1).map(n => n.name).join(' > ')
          : '';
        
        // Only include nodes with segmentType === SEGMENT_TYPE.LEAF
        if (node.segmentType === SEGMENT_TYPE.LEAF) {
          const entry = {
            name: node.name,
            id: node.id,
            subcategory: pathString,
            subcategoryId: node.id,
            fullPath: currentPath.map(n => n.name).join(' > '),
            node,
          };
          results.push(entry);
        }

        if (node.subcategories) {
          flattenTree(node.subcategories, currentPath, results);
        }
      });
      return results;
    };

    // If isGlobalSearch is false but no subcategory is selected, 
    // we should search the entire tree
    const subcategories = isGlobalSearch || !selectedSubcategory
      ? data.subcategories || []
      : [selectedSubcategory];

    const flattened = flattenTree(subcategories);

    // If we have no data to search in, return empty array
    if (!flattened.length) {
      return { 
        searchResults: [], 
        showNoResultsMessage: trimmedSearchTerm.length >= 3
      };
    }

    const fuse = new Fuse(flattened, {
      keys: ['name'],
      threshold: 0.6,
      ignoreLocation: true,
      includeScore: true,
      minMatchCharLength: 3,
    });

    const results = fuse.search(trimmedSearchTerm);
    
    // Calculate if we should show no results message
    const shouldShowNoResultsMessage = trimmedSearchTerm.length >= 3 && results.length === 0;
    
    // Return empty array if no results found
    if (!results.length) {
      return { 
        searchResults: [], 
        showNoResultsMessage: shouldShowNoResultsMessage
      };
    }
    
    // Only return results with a reasonable match score
    const filteredResults = results
      .filter(result => result.score < 0.6) // Lower score means better match
      .map(result => result.item);
    
    // Should show no results message if we have trimmed search term long enough 
    // but no filtered results after score filtering
    const showNoResults = trimmedSearchTerm.length >= 3 && filteredResults.length === 0;
    
    return { 
      searchResults: filteredResults.slice(0, 10), 
      showNoResultsMessage: showNoResults
    };
  }, [searchTerm, data, selectedSubcategory, isGlobalSearch, nodePathMap]);

  const handleSearch = useCallback((e) => {
    // Ensure e and e.target exist before accessing value
    if (e && e.target && e.target.value !== undefined) {
      setSearchTerm(e.target.value);
      setShowResults(true);
    } else {
      console.warn('Invalid event passed to handleSearch:', e);
      // Default to empty string if event structure is invalid
      setSearchTerm('');
      setShowResults(false);
    }
  }, []);

  const handleResultSelect = useCallback((result) => {
    setSearchTerm('');
    setShowResults(false);
    
    // Make sure result and data exist
    if (!result || !data) {
      return { selectedNode: null, pathToNode: [] };
    }
    
    const categoryPath = findCategoryPath(data.subcategories || [], result);
    const pathIds = data.id ? [data.id, ...(categoryPath ? categoryPath.map(node => node.id) : [])] : [];
    
    return {
      selectedNode: result,
      pathToNode: pathIds
    };
  }, [data]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setShowResults(false);
  }, []);

  return {
    searchTerm,
    searchResults,
    showResults,
    isGlobalSearch,
    handleSearch,
    handleResultSelect,
    setIsGlobalSearch,
    showNoResultsMessage,
    clearSearch,
  };
};

export default useSearch;
